import { useState, useCallback } from "react";
import Passwordless from "supertokens-auth-react/recipe/passwordless";
import { handleApiError, getErrorMessage } from "../utils/errorHandler";
import { UseSupertokensReturn } from "../types/supertokens";

export const useSupertokens = (): UseSupertokensReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);

  const sendCode = useCallback(async (email: string) => {
    setIsLoading(true);
    setMessage("");

    try {
      const response = await Passwordless.createCode({
        email,
      });

      if (response.status === "OK") {
        setIsCodeSent(true);
        setMessage(
          "Check your email for the magic link and code! Click the magic link in your email to verify and access the photo selection page."
        );
      } else {
        setMessage("Failed to send code. Please try again.");
      }
    } catch (error) {
      const appError = handleApiError(error);
      console.error("Error sending code:", appError);
      setMessage(`Error: ${getErrorMessage(appError)}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyCode = useCallback(async (code: string): Promise<boolean> => {
    setIsLoading(true);
    setMessage("");

    try {
      const response = await Passwordless.consumeCode({
        userInputCode: code,
      });

      if (response.status === "OK") {
        setMessage("Code verified successfully! Redirecting to photo selection...");
        return true;
      } else {
        setMessage("Code verification failed. Please try again.");
        return false;
      }
    } catch (error) {
      const appError = handleApiError(error);
      console.error("Error verifying code:", appError);
      setMessage(`Error: ${getErrorMessage(appError)}`);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetState = useCallback(() => {
    setIsCodeSent(false);
    setMessage("");
    setIsLoading(false);
  }, []);

  return {
    isLoading,
    message,
    isCodeSent,
    sendCode,
    verifyCode,
    resetState,
  };
};
